import { useQuery } from 'react-query'
import QUERIES from 'api/queries'
import {
	getIntegrationById,
	getIntegrations,
	getIntegrationStatistic,
	getSupportedBlockchains,
	getVezgoSecret,
	getVezgoToken,
	getWalletById,
	getWallets,
} from './services'
import { IIntegration, IWallet } from './models'
import { useMemo } from 'react'

export const useIntegrations = () => {
	const { data: wallets = [] } = useWallets()

	const query = useQuery(QUERIES.integrations, async () => {
		const integrations = await getIntegrations()
		// sort by date descending
		return integrations.sort((prev, next) => (prev.integrationStartedAt < next.integrationStartedAt ? -1 : 1))
	})

	query.data = useMemo(
		() =>
			query.data?.map(integration => ({
				...integration,
				wallets: integration.walletIds
					.map(walletId => {
						const foundWallet = wallets.find(({ id }) => id === walletId)!

						return foundWallet
					})
					.filter(item => !!item),
			})),
		[query.data, wallets]
	)

	return {
		...query,
		integrationOptions: useMemo(
			() =>
				query.data?.map(({ name, id }) => ({
					value: id,
					label: name,
				})) || [],
			[query.data]
		),
	}
}

export const useIntegrationById = (id: IIntegration['id']) => {
	const { data: wallets = [] } = useWallets()

	return useQuery([QUERIES.integrationById, id], async () => {
		const integration = await getIntegrationById(id)

		if (typeof integration.wallets?.[0] === 'string') {
			integration.wallets = (integration.wallets as unknown as string[])
				.map(walletId => {
					const foundWallet = wallets.find(item => item.id === walletId)!

					return foundWallet
				})
				.filter(item => !!item)
		}

		return integration
	})
}

export const useIntegrationStatistic = () => useQuery(QUERIES.integrationStatistic, getIntegrationStatistic)

export const useWallets = () => {
	const query = useQuery(QUERIES.wallets, getWallets)

	return {
		...query,
		walletOptions: useMemo(
			() =>
				query.data?.map(({ name, id }) => ({
					value: id,
					label: name,
				})) || [],
			[query.data]
		),
	}
}

export const useWalletById = (walletId?: IWallet['id']) =>
	useQuery([QUERIES.walletById, walletId], async () => (walletId ? await getWalletById(walletId) : undefined), {
		enabled: !!walletId,
	})

export const useSupportedBlockchains = () => useQuery(QUERIES.supportedBlockchains, getSupportedBlockchains)

export const useVezgoToken = () => useQuery(QUERIES.vezgoToken, getVezgoToken)

export const useVezgoSecret = () => useQuery(QUERIES.vezgoSecret, getVezgoSecret)
